export const ANALYTICS_CONSENT_STORAGE_KEY = 'bundle-drop.analytics-consent.v1';
export const ANALYTICS_CONSENT_VERSION = 1;
export const ANALYTICS_CONSENT_DURATION_MS = 365 * 24 * 60 * 60 * 1000;

export type AnalyticsConsentStatus = 'unknown' | 'granted' | 'denied';
type PersistedAnalyticsConsentStatus = Exclude<AnalyticsConsentStatus, 'unknown'>;

type AnalyticsConsentRecord = {
  version: typeof ANALYTICS_CONSENT_VERSION;
  status: PersistedAnalyticsConsentStatus;
  updatedAt: number;
};

type StorageLike = Pick<Storage, 'clear' | 'getItem' | 'removeItem' | 'setItem'>;

const getBrowserStorage = (): StorageLike | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const parseAnalyticsConsentRecord = (
  serializedRecord: string | null,
  now: number
): AnalyticsConsentRecord | null => {
  if (!serializedRecord) {
    return null;
  }

  try {
    const record = JSON.parse(serializedRecord) as Partial<AnalyticsConsentRecord>;
    const hasValidStatus = record.status === 'granted' || record.status === 'denied';
    const hasValidTimestamp =
      typeof record.updatedAt === 'number' &&
      Number.isFinite(record.updatedAt) &&
      record.updatedAt <= now &&
      now - record.updatedAt < ANALYTICS_CONSENT_DURATION_MS;

    if (
      record.version !== ANALYTICS_CONSENT_VERSION ||
      !hasValidStatus ||
      !hasValidTimestamp
    ) {
      return null;
    }

    return record as AnalyticsConsentRecord;
  } catch {
    return null;
  }
};

export const getAnalyticsConsentStatus = (
  now = Date.now(),
  storage: StorageLike | null = getBrowserStorage()
): AnalyticsConsentStatus => {
  if (!storage) {
    return 'unknown';
  }

  try {
    return parseAnalyticsConsentRecord(
      storage.getItem(ANALYTICS_CONSENT_STORAGE_KEY),
      now
    )?.status ?? 'unknown';
  } catch {
    return 'unknown';
  }
};

export const persistAnalyticsConsentStatus = (
  status: PersistedAnalyticsConsentStatus,
  now = Date.now(),
  storage: StorageLike | null = getBrowserStorage()
): void => {
  if (!storage) {
    return;
  }

  const record: AnalyticsConsentRecord = {
    version: ANALYTICS_CONSENT_VERSION,
    status,
    updatedAt: now,
  };

  try {
    storage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Analytics consent should never make the application unusable.
  }
};

export const clearStoragePreservingAnalyticsConsent = (
  now = Date.now(),
  storage: StorageLike | null = getBrowserStorage()
): void => {
  if (!storage) {
    return;
  }

  let consentRecord: string | null = null;

  try {
    const storedRecord = storage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    if (parseAnalyticsConsentRecord(storedRecord, now)) {
      consentRecord = storedRecord;
    }

    storage.clear();

    if (consentRecord) {
      storage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, consentRecord);
    }
  } catch {
    // Logout must continue even when browser storage is unavailable.
  }
};

export const isAnalyticsConsentGranted = (): boolean =>
  getAnalyticsConsentStatus() === 'granted';
